import type { ImageCategory, StrainImage } from '@shared';
import { createClientId } from '../createClientId';
import { ProductRepositoryError } from '../productRepository/errors';
import { createIdbProductImageStore } from './idbProductImageStore';
import {
  localRecordToStrainImage,
  sortLocalImageRecords,
} from './mapToStrainImage';
import type {
  AddLocalProductImageInput,
  LocalProductImageRecord,
  ProductImageStore,
} from './types';
import { validateLocalImageBlob } from './validate';

export interface LocalProductImageRepository {
  list(productId: string): Promise<StrainImage[]>;
  listRecords(productId: string): Promise<LocalProductImageRecord[]>;
  add(input: AddLocalProductImageInput): Promise<LocalProductImageRecord>;
  remove(imageId: string): Promise<{ deleted: boolean; productId: string }>;
  setPrimary(imageId: string): Promise<LocalProductImageRecord>;
  deleteAllForProduct(productId: string): Promise<number>;
}

export interface LocalProductImageRepositoryOptions {
  store?: ProductImageStore;
  createId?: () => string;
}

export function createLocalProductImageRepository(
  options: LocalProductImageRepositoryOptions = {},
): LocalProductImageRepository {
  const store = options.store ?? createIdbProductImageStore();
  const createId = options.createId ?? createClientId;

  async function listRecords(
    productId: string,
  ): Promise<LocalProductImageRecord[]> {
    return sortLocalImageRecords(await store.listByProductId(productId));
  }

  return {
    listRecords,

    async list(productId: string) {
      const records = await listRecords(productId);
      return records.map(localRecordToStrainImage);
    },

    async add(input: AddLocalProductImageInput) {
      validateLocalImageBlob(input.blob);
      const existing = await listRecords(input.productId);
      const id = createId();
      const collision = await store.get(id);
      if (collision) {
        throw ProductRepositoryError.conflict(
          'Generated image id already exists; refusing to overwrite.',
        );
      }
      const sortOrder =
        existing.length === 0
          ? 0
          : Math.max(...existing.map((r) => r.sortOrder)) + 1;
      const record: LocalProductImageRecord = {
        id,
        productId: input.productId,
        blob: input.blob,
        mimeType: input.blob.type,
        originalFilename: input.originalFilename ?? null,
        createdAt: new Date().toISOString(),
        sortOrder,
        isPrimary: existing.length === 0,
        imageCategory: input.imageCategory,
        width: input.width ?? null,
        height: input.height ?? null,
      };
      await store.put(record);
      return record;
    },

    async remove(imageId: string) {
      const existing = await store.get(imageId);
      if (!existing) {
        throw ProductRepositoryError.notFound('Image not found');
      }
      const productId = existing.productId;
      const siblings = await listRecords(productId);
      const remaining = siblings.filter((row) => row.id !== imageId);
      const promote =
        existing.isPrimary && remaining.length > 0
          ? { ...remaining[0]!, isPrimary: true }
          : null;
      await store.applyBatch({
        deleteIds: [imageId],
        put: promote ? [promote] : [],
      });
      return { deleted: true, productId };
    },

    async setPrimary(imageId: string) {
      const existing = await store.get(imageId);
      if (!existing) {
        throw ProductRepositoryError.notFound('Image not found');
      }
      const siblings = await listRecords(existing.productId);
      const updatedRows = siblings.map((row) => ({
        ...row,
        isPrimary: row.id === imageId,
      }));
      await store.applyBatch({ put: updatedRows });
      const updated = updatedRows.find((row) => row.id === imageId);
      if (!updated) {
        throw ProductRepositoryError.notFound('Image not found');
      }
      return updated;
    },

    async deleteAllForProduct(productId: string) {
      return store.deleteAllForProduct(productId);
    },
  };
}

export type { ImageCategory };
